<script setup>
import { ref, watch } from 'vue';
import ConditionEditor from './ConditionEditor.vue';

const props = defineProps({
  step: { type: Object, required: true },
  availableBots: { type: Array, default: () => [] },
});
const emit = defineEmits(['save', 'close']);

const VARIABLES = [
  '{{contact.name}}', '{{contact.phone}}', '{{contact.email}}',
  '{{deal.title}}', '{{deal.value}}', '{{deal.stage}}', '{{deal.pipeline}}',
];

const ACTION_TYPES = [
  { key: 'add_label',       label: 'Agregar etiqueta',       fields: [{ key: 'labels', label: 'Etiquetas (separadas por coma)' }] },
  { key: 'remove_label',    label: 'Quitar etiqueta',        fields: [{ key: 'labels', label: 'Etiquetas (separadas por coma)' }] },
  { key: 'assign_agent',    label: 'Asignar agente',         fields: [{ key: 'agent_id',  label: 'ID del agente' }] },
  { key: 'assign_team',     label: 'Asignar equipo',         fields: [{ key: 'team_id',   label: 'ID del equipo' }] },
  { key: 'change_stage',    label: 'Mover a etapa',          fields: [{ key: 'stage_id',  label: 'ID de la etapa' }] },
  { key: 'change_priority', label: 'Cambiar prioridad',      fields: [{ key: 'priority',  label: 'Prioridad', options: ['low','medium','high','urgent'] }] },
  { key: 'resolve',         label: 'Resolver conversación',  fields: [] },
  { key: 'snooze',          label: 'Posponer',               fields: [{ key: 'hours', label: 'Horas' }] },
];

const config = ref({});
const messageRef = ref(null);
const rawJson = ref('{}');

watch(() => props.step, (s) => {
  config.value = JSON.parse(JSON.stringify(s.config || {}));
  if (s.step_type === 'list_message') {
    rawJson.value = JSON.stringify(s.config?.sections || [], null, 2);
  } else {
    rawJson.value = JSON.stringify(s.config || {}, null, 2);
  }
}, { immediate: true });

const TYPES_WITH_FORM = ['message','pause','condition','action','reaction','comment','round_robin','validation','subscribe_meta','widget','code','subbot'];
const VALIDATION_VAR = '{{context.last_validation_value}}';
const CONTEXT_VAR_HINT = '{{context.clave}}';

const CODE_TEMPLATE = `# Disponible: contact, conversation, bot, run\n# Helpers: get('clave'), set('clave', valor), log('mensaje')\n# Timeout: 5 segundos\n\n# Ejemplo: guardar día de la semana en context\nset('weekday', Time.current.strftime('%A'))\nlog("weekday=#{get('weekday')}")\n`;

function save() {
  let toSave = JSON.parse(JSON.stringify(config.value || {}));

  // list_message: persiste sections desde rawJson además de header/body/footer
  if (props.step.step_type === 'list_message') {
    try { toSave.sections = JSON.parse(rawJson.value || '[]'); } catch { toSave.sections = []; }
  } else if (!TYPES_WITH_FORM.includes(props.step.step_type)) {
    try { toSave = JSON.parse(rawJson.value || '{}'); } catch { /* keep config */ }
  }
  emit('save', { step: props.step, config: toSave });
}

function setRange(idx, value) {
  if (!Array.isArray(config.value.range)) config.value.range = [null, null];
  config.value.range[idx] = value === '' ? null : Number(value);
}

function insertVariable(v) {
  const el = messageRef.value;
  if (!el) {
    config.value.content = (config.value.content || '') + v;
    return;
  }
  const start = el.selectionStart;
  const end = el.selectionEnd;
  const text = config.value.content || '';
  config.value.content = text.slice(0, start) + v + text.slice(end);
  setTimeout(() => { el.focus(); el.selectionStart = el.selectionEnd = start + v.length; }, 0);
}

function actionFields() {
  return ACTION_TYPES.find(a => a.key === config.value.action_type)?.fields || [];
}
</script>

<template>
  <div class="bg-n-background border-l border-n-weak w-[360px] flex flex-col">
    <div class="px-4 py-3 border-b border-n-weak flex items-center justify-between">
      <div class="text-sm font-semibold text-n-slate-12">Configurar paso #{{ step.id }}</div>
      <button class="text-n-slate-11 hover:text-n-slate-12 text-xl leading-none" @click="emit('close')">×</button>
    </div>

    <div class="flex-1 overflow-y-auto p-4 space-y-4">
      <!-- MENSAJE -->
      <template v-if="step.step_type === 'message'">
        <div>
          <label class="block text-xs font-medium text-n-slate-12 mb-1">Contenido</label>
          <textarea
            ref="messageRef"
            v-model="config.content"
            rows="6"
            class="w-full px-3 py-2 rounded-md border border-n-strong text-sm font-mono"
            placeholder="Hola, gracias por escribir…"
          />
          <div class="flex flex-wrap gap-1 mt-2">
            <button
              v-for="v in VARIABLES"
              :key="v"
              type="button"
              class="px-2 py-1 text-[11px] rounded-md bg-n-alpha-black2 hover:bg-n-alpha-black3 text-n-slate-12 border border-n-weak"
              @click="insertVariable(v)"
            >
              {{ v }}
            </button>
          </div>
          <!-- Variar con IA · ChatGPT reescribe el mensaje distinto cada vez -->
          <label class="flex items-center gap-2 mt-3 cursor-pointer select-none">
            <input v-model="config.ai_variation" type="checkbox" class="rounded border-n-strong" />
            <span class="text-xs font-medium text-n-slate-12">✦ Variar con IA</span>
          </label>
          <p v-if="config.ai_variation" class="text-[11px] text-n-slate-11 mt-1">
            ChatGPT reescribirá este mensaje distinto cada vez, conservando los datos (dirección, links, horarios). Si la IA falla, se envía el texto original.
          </p>
        </div>
      </template>

      <!-- PAUSA -->
      <template v-else-if="step.step_type === 'pause'">
        <div class="grid grid-cols-3 gap-2">
          <div>
            <label class="block text-xs font-medium text-n-slate-12 mb-1">Horas</label>
            <input v-model.number="config.hours" type="number" min="0" class="w-full px-2 py-1.5 rounded-md border border-n-strong text-sm" />
          </div>
          <div>
            <label class="block text-xs font-medium text-n-slate-12 mb-1">Minutos</label>
            <input v-model.number="config.minutes" type="number" min="0" max="59" class="w-full px-2 py-1.5 rounded-md border border-n-strong text-sm" />
          </div>
          <div>
            <label class="block text-xs font-medium text-n-slate-12 mb-1">Segundos</label>
            <input v-model.number="config.seconds" type="number" min="0" max="59" class="w-full px-2 py-1.5 rounded-md border border-n-strong text-sm" />
          </div>
        </div>
        <p class="text-[11px] text-n-slate-11">El bot esperará este tiempo antes de continuar al siguiente paso.</p>
      </template>

      <!-- CONDICIÓN · editor estilo Kommo con dropdown de variables -->
      <template v-else-if="step.step_type === 'condition'">
        <ConditionEditor v-model="config" />
        <p class="text-[11px] text-n-slate-11 mt-2">El bot ramificará en "Sí" si CUALQUIER bloque de condiciones se cumple. En "No" si ninguno se cumple.</p>
      </template>

      <!-- ACCIONES -->
      <template v-else-if="step.step_type === 'action'">
        <div>
          <label class="block text-xs font-medium text-n-slate-12 mb-1">Tipo de acción</label>
          <select v-model="config.action_type" class="w-full px-2 py-1.5 rounded-md border border-n-strong text-sm">
            <option v-for="a in ACTION_TYPES" :key="a.key" :value="a.key">{{ a.label }}</option>
          </select>
        </div>
        <div v-for="f in actionFields()" :key="f.key">
          <label class="block text-xs font-medium text-n-slate-12 mb-1">{{ f.label }}</label>
          <select v-if="f.options" v-model="config[f.key]" class="w-full px-2 py-1.5 rounded-md border border-n-strong text-sm">
            <option v-for="o in f.options" :key="o" :value="o">{{ o }}</option>
          </select>
          <input v-else v-model="config[f.key]" type="text" class="w-full px-2 py-1.5 rounded-md border border-n-strong text-sm" />
        </div>
      </template>

      <!-- REACCIÓN -->
      <template v-else-if="step.step_type === 'reaction'">
        <div>
          <label class="block text-xs font-medium text-n-slate-12 mb-1">Emoji a reaccionar</label>
          <input v-model="config.emoji" type="text" maxlength="4" class="w-24 px-2 py-1.5 rounded-md border border-n-strong text-2xl text-center" placeholder="❤" />
          <p class="text-[11px] text-n-slate-11 mt-1">Se enviará como mensaje al cliente. Funciona en WhatsApp e Instagram.</p>
        </div>
      </template>

      <!-- COMENTARIO (nota privada para agentes) -->
      <template v-else-if="step.step_type === 'comment'">
        <div>
          <label class="block text-xs font-medium text-n-slate-12 mb-1">Nota privada (sólo agentes la ven)</label>
          <textarea v-model="config.content" rows="5" class="w-full px-3 py-2 rounded-md border border-n-strong text-sm" placeholder="Lead caliente — confirmar precio antes del lunes" />
        </div>
      </template>

      <!-- LIST MESSAGE (WhatsApp interactive list) -->
      <template v-else-if="step.step_type === 'list_message'">
        <div>
          <label class="block text-xs font-medium text-n-slate-12 mb-1">Encabezado</label>
          <input v-model="config.header" type="text" class="w-full px-2 py-1.5 rounded-md border border-n-strong text-sm" placeholder="Catálogo 2026" />
        </div>
        <div>
          <label class="block text-xs font-medium text-n-slate-12 mb-1">Cuerpo</label>
          <textarea v-model="config.body" rows="3" class="w-full px-3 py-2 rounded-md border border-n-strong text-sm" placeholder="Elige el plan que más te interesa:" />
        </div>
        <div>
          <label class="block text-xs font-medium text-n-slate-12 mb-1">Pie</label>
          <input v-model="config.footer" type="text" class="w-full px-2 py-1.5 rounded-md border border-n-strong text-sm" placeholder="Víctor Guzmán Fotografía" />
        </div>
        <div>
          <label class="block text-xs font-medium text-n-slate-12 mb-1">Secciones (JSON)</label>
          <textarea v-model="rawJson" rows="6" class="w-full px-2 py-1.5 rounded-md border border-n-strong text-xs font-mono"
            placeholder='[{"title":"Sesiones","items":[{"title":"15 años","description":"Desde RD$15,000"}]}]' />
          <p class="text-[10px] text-n-slate-11 mt-1">Las secciones se renderizan como lista. Sintaxis JSON.</p>
        </div>
      </template>

      <!-- ROUND ROBIN -->
      <template v-else-if="step.step_type === 'round_robin'">
        <div>
          <label class="block text-xs font-medium text-n-slate-12 mb-1">Equipo (opcional)</label>
          <input v-model.number="config.team_id" type="number" class="w-full px-2 py-1.5 rounded-md border border-n-strong text-sm" placeholder="ID del equipo, vacío = administradores" />
          <p class="text-[11px] text-n-slate-11 mt-1">Si dejas vacío, rota entre los administradores de la cuenta. Si pones un team_id, rota entre los miembros del equipo.</p>
        </div>
      </template>

      <!-- VALIDACIÓN -->
      <template v-else-if="step.step_type === 'validation'">
        <div>
          <label class="block text-xs font-medium text-n-slate-12 mb-1">Tipo esperado</label>
          <select v-model="config.expect" class="w-full px-2 py-1.5 rounded-md border border-n-strong text-sm">
            <option value="any">Cualquier respuesta</option>
            <option value="number">Número</option>
            <option value="email">Email válido</option>
            <option value="phone">Teléfono</option>
            <option value="regex">Regex personalizado</option>
          </select>
        </div>
        <div v-if="config.expect === 'number'">
          <label class="block text-xs font-medium text-n-slate-12 mb-1">Rango (opcional)</label>
          <div class="grid grid-cols-2 gap-2">
            <input :value="config.range?.[0]" type="number" class="px-2 py-1.5 rounded-md border border-n-strong text-sm" placeholder="Mín" @input="setRange(0, $event.target.value)" />
            <input :value="config.range?.[1]" type="number" class="px-2 py-1.5 rounded-md border border-n-strong text-sm" placeholder="Máx" @input="setRange(1, $event.target.value)" />
          </div>
        </div>
        <div v-if="config.expect === 'regex'">
          <label class="block text-xs font-medium text-n-slate-12 mb-1">Patrón regex</label>
          <input v-model="config.pattern" type="text" class="w-full px-2 py-1.5 rounded-md border border-n-strong text-sm font-mono" placeholder="^\\d{4,}$" />
        </div>
        <div>
          <label class="block text-xs font-medium text-n-slate-12 mb-1">Timeout (minutos)</label>
          <input v-model.number="config.timeout_minutes" type="number" min="1" class="w-32 px-2 py-1.5 rounded-md border border-n-strong text-sm" placeholder="60" />
          <p class="text-[11px] text-n-slate-11 mt-1">Si no recibe respuesta válida en este tiempo, el run falla. La respuesta válida se guarda en <code>{{ VALIDATION_VAR }}</code>.</p>
        </div>
      </template>

      <!-- SUSCRIBIRSE (Meta) -->
      <template v-else-if="step.step_type === 'subscribe_meta'">
        <div>
          <label class="block text-xs font-medium text-n-slate-12 mb-1">Lista</label>
          <input v-model="config.list" type="text" class="w-full px-2 py-1.5 rounded-md border border-n-strong text-sm" placeholder="broadcasts" />
          <p class="text-[11px] text-n-slate-11 mt-1">Aplica una etiqueta <code>subscribed-&lt;lista&gt;</code> a la conversación para suscripción a difusiones.</p>
        </div>
      </template>

      <!-- WIDGET -->
      <template v-else-if="step.step_type === 'widget'">
        <div>
          <label class="block text-xs font-medium text-n-slate-12 mb-1">Tipo de widget</label>
          <select v-model="config.widget_type" class="w-full px-2 py-1.5 rounded-md border border-n-strong text-sm">
            <option value="calendar">Calendario / Booking</option>
            <option value="form">Formulario</option>
            <option value="payment">Pago</option>
            <option value="other">Otro</option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-medium text-n-slate-12 mb-1">Texto introductorio</label>
          <input v-model="config.intro" type="text" class="w-full px-2 py-1.5 rounded-md border border-n-strong text-sm" placeholder="Reserva tu cita aquí:" />
        </div>
        <div>
          <label class="block text-xs font-medium text-n-slate-12 mb-1">URL del widget</label>
          <input v-model="config.url" type="text" class="w-full px-2 py-1.5 rounded-md border border-n-strong text-sm" placeholder="https://cal.com/victorguzman" />
        </div>
      </template>

      <!-- CODE (Ruby step) -->
      <template v-else-if="step.step_type === 'code'">
        <div class="px-2 py-2 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-800">
          ⚠️ <strong>Sólo para administradores.</strong> El código se ejecuta en el proceso de Rails con timeout de 5s. Usa <code>set('clave', valor)</code> para guardar valores en context y leerlos después con <code>{{ CONTEXT_VAR_HINT }}</code>.
        </div>
        <div>
          <label class="block text-xs font-medium text-n-slate-12 mb-1">Código Ruby</label>
          <textarea v-model="config.code" rows="14" class="w-full px-3 py-2 rounded-md border border-n-strong text-xs font-mono leading-relaxed" :placeholder="CODE_TEMPLATE" />
          <p class="text-[10px] text-n-slate-11 mt-1">Variables disponibles: <code>contact</code>, <code>conversation</code>, <code>bot</code>, <code>run</code>. Helpers: <code>get('k')</code>, <code>set('k', v)</code>, <code>log('msg')</code>.</p>
        </div>
      </template>

      <!-- SUBBOT (Iniciar Salesbot anidado) -->
      <template v-else-if="step.step_type === 'subbot'">
        <div>
          <label class="block text-xs font-medium text-n-slate-12 mb-1">Bot a lanzar</label>
          <select v-model.number="config.bot_id" class="w-full px-2 py-1.5 rounded-md border border-n-strong text-sm">
            <option :value="null">— Selecciona un bot —</option>
            <option v-for="b in availableBots" :key="b.id" :value="b.id">
              {{ b.name }} ({{ b.active ? 'activo' : 'pausado' }})
            </option>
          </select>
          <p v-if="!availableBots.length" class="text-[11px] text-amber-700 mt-1">No hay otros bots en la cuenta. Crea uno desde la lista para poder lanzarlo desde aquí.</p>
        </div>
        <div class="px-2 py-2 bg-n-alpha-black1 border border-n-weak rounded text-[11px] text-n-slate-11">
          ⏸️ <strong>El flujo principal se detiene aquí</strong> y solo continúa cuando este sub-bot termina todos sus pasos (incluidas sus propias pausas). Comportamiento automático en todos los sub-bots.
        </div>
      </template>

      <!-- fallback (no debería pasar dado que TYPES_WITH_FORM cubre todos) -->
      <template v-else>
        <div class="text-[11px] text-n-slate-11 mb-2">Tipo: <strong>{{ step.step_type }}</strong> — config JSON cruda</div>
        <textarea v-model="rawJson" rows="8" class="w-full px-2 py-1.5 rounded-md border border-n-strong text-xs font-mono" />
      </template>
    </div>

    <div class="px-4 py-3 border-t border-n-weak flex justify-end gap-2">
      <button class="px-3 py-1.5 text-xs rounded-md hover:bg-n-alpha-black2" @click="emit('close')">Cancelar</button>
      <button class="px-3 py-1.5 text-xs rounded-md bg-blue-600 text-white hover:bg-blue-700" @click="save">Guardar</button>
    </div>
  </div>
</template>
